import { assetBlob } from './localAssets.ts'
import { splitAlpha } from './util/color.ts'
import { nodesBounds } from './scene/geometry.ts'
import { orderedNodes, scene, selectedNodes } from './scene/store.ts'
import type { Rect, SceneNode } from './scene/types.ts'
import { polylineD, smoothStrokeD } from './util/path.ts'

const THUMBNAIL_MAX = 480

export interface ExportOptions {
  /** Export only the current selection. */
  selectionOnly?: boolean
  padding?: number
  /** Paint the scene background behind the content. */
  background?: boolean
}

export interface SvgExport {
  svg: string
  width: number
  height: number
}

function esc(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

const deg = (radians: number) => (radians * 180) / Math.PI

async function assetDataUri(assetId: string): Promise<string> {
  const blob = await assetBlob(assetId)
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })
}

/** Split #rrggbbaa into a color plus an opacity attribute; not every SVG reader takes 8-digit hex. */
function paint(attr: 'fill' | 'stroke', color: string): string {
  const { rgb, alpha } = splitAlpha(color)
  return ` ${attr}="${esc(rgb)}"` + (alpha === 1 ? '' : ` ${attr}-opacity="${+alpha.toFixed(3)}"`)
}

function nodeMarkup(node: SceneNode, images: Map<string, string>): string {
  const open = `<g transform="translate(${node.x} ${node.y}) rotate(${deg(node.rotation)} ${node.w / 2} ${node.h / 2})"${
    node.opacity === 1 ? '' : ` opacity="${node.opacity}"`
  }>`

  let inner = ''
  switch (node.type) {
    case 'rect':
      inner =
        `<rect width="${node.w}" height="${node.h}" rx="${node.cornerRadius}" ry="${node.cornerRadius}"` +
        `${paint('fill', node.fill)}${paint('stroke', node.stroke)} stroke-width="${node.strokeWidth}"/>`
      break

    case 'ellipse':
      inner =
        `<ellipse cx="${node.w / 2}" cy="${node.h / 2}" rx="${node.w / 2}" ry="${node.h / 2}"` +
        `${paint('fill', node.fill)}${paint('stroke', node.stroke)} stroke-width="${node.strokeWidth}"/>`
      break

    case 'line':
    case 'path': {
      const d =
        node.type === 'path' ? smoothStrokeD(node.points) : polylineD(node.points, node.closed)
      // A nested svg reproduces the viewBox stretch the editor renders with.
      inner =
        `<svg x="0" y="0" width="${node.w}" height="${node.h}" viewBox="0 0 ${node.viewW} ${node.viewH}"` +
        ` preserveAspectRatio="none" overflow="visible">` +
        `<path d="${d}"${
          node.type === 'line' && node.closed && node.fill ? paint('fill', node.fill) : ' fill="none"'
        }${paint('stroke', node.stroke)} stroke-width="${node.strokeWidth}"` +
        ` stroke-linecap="round" stroke-linejoin="round"/></svg>`
      break
    }

    case 'image': {
      const href = images.get(node.assetId)
      if (!href) return ''
      inner =
        `<image x="0" y="0" width="${node.w}" height="${node.h}" preserveAspectRatio="none"` +
        ` href="${href}"/>`
      break
    }

    case 'text': {
      if (node.text.trim() === '') return ''
      const lineHeight = node.fontSize * 1.3
      // Approximate the DOM's first baseline for line-height 1.3.
      const firstBaseline = node.fontSize * 0.95
      const anchor =
        node.align === 'center' ? 'middle' : node.align === 'right' ? 'end' : 'start'
      const x = node.align === 'center' ? node.w / 2 : node.align === 'right' ? node.w : 0

      const lines = node.text.split('\n')
      const tspans = lines
        .map(
          (line, i) =>
            `<tspan x="${x}" y="${firstBaseline + i * lineHeight}">${esc(line) || ' '}</tspan>`,
        )
        .join('')

      inner =
        `<text font-family="${esc(node.fontFamily)}" font-size="${node.fontSize}"` +
        `${paint('fill', node.color)} text-anchor="${anchor}"` +
        ` xml:space="preserve">${tspans}</text>`
      break
    }
  }

  return inner === '' ? '' : `${open}${inner}</g>`
}

export async function sceneToSvg(opts: ExportOptions = {}): Promise<SvgExport | null> {
  const source = opts.selectionOnly ? selectedNodes() : orderedNodes()
  const nodes = source.filter((n) => !n.hidden)
  if (nodes.length === 0) return null

  const bounds = nodesBounds(nodes)
  if (!bounds) return null

  const padding = opts.padding ?? 24
  const box: Rect = {
    x: bounds.x - padding,
    y: bounds.y - padding,
    w: Math.max(1, bounds.w + padding * 2),
    h: Math.max(1, bounds.h + padding * 2),
  }

  // Inline every image so the file stands alone.
  const images = new Map<string, string>()
  for (const node of nodes) {
    if (node.type === 'image' && !images.has(node.assetId)) {
      images.set(node.assetId, await assetDataUri(node.assetId))
    }
  }

  const bg =
    opts.background === false
      ? ''
      : `<rect x="${box.x}" y="${box.y}" width="${box.w}" height="${box.h}"${paint('fill', scene.background)}/>`

  const body = nodes.map((n) => nodeMarkup(n, images)).join('')

  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"` +
    ` width="${box.w}" height="${box.h}" viewBox="${box.x} ${box.y} ${box.w} ${box.h}">` +
    `${bg}${body}</svg>`

  return { svg, width: box.w, height: box.h }
}

export async function svgToPng(
  svg: string,
  width: number,
  height: number,
  scale = 2,
): Promise<Blob> {
  const img = new Image()
  // A data URI keeps the canvas untainted; every asset is already inlined.
  img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
  await img.decode()

  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(width * scale))
  canvas.height = Math.max(1, Math.round(height * scale))

  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('No 2D context for PNG export')
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

  return await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('toBlob failed'))), 'image/png'),
  )
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  // Revoking immediately can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

export async function exportSvg(name: string, opts: ExportOptions = {}): Promise<boolean> {
  const result = await sceneToSvg(opts)
  if (!result) return false
  downloadBlob(new Blob([result.svg], { type: 'image/svg+xml' }), `${name}.svg`)
  return true
}

export async function exportPng(name: string, opts: ExportOptions = {}): Promise<boolean> {
  const result = await sceneToSvg(opts)
  if (!result) return false
  const png = await svgToPng(result.svg, result.width, result.height, 2)
  downloadBlob(png, `${name}.png`)
  return true
}

/** Small PNG of the whole scene, used as the project's thumbnail. */
export async function renderThumbnail(): Promise<Blob | null> {
  const result = await sceneToSvg({ padding: 8 })
  if (!result) return null
  const scale = Math.min(1, THUMBNAIL_MAX / Math.max(result.width, result.height))
  return await svgToPng(result.svg, result.width, result.height, scale)
}
